import React from 'react'
import {
    useUltraPlayStore,
    useConfigurationStore,
    useBetSliderStore,
    useSpinButtonSectionStore,
    usePlayerInfoStore,
    useBuyBonusStore,
    useDoubleChanceStore,
    useJackpotStore,
    useSoundStore,
    ULTRA_PLAY_SPIN_OPTIONS,
    ULTRA_PLAY_DOUBLE_CHANCE,
} from '@stores'
import { useThrottledCallback } from '@hooks'
import { SOUNDS } from '@constants/sounds'
import {
    AutoplayButtonClickedMessage,
    BetSliderSelectedIndexChangedMessage,
    BetOptionItem,
    BuyBonusButtonClickedMessage,
    BuyBonusConfirmButtonClickedMessage,
    BuyBonusOptionButtonClickedMessage,
    DoubleChanceButtonClickedMessage,
    EngineBetSliderSetScrollingIndexMessage,
    ExtraWinMessages,
    ExtraWinToggledMessage,
    GameUIMessageScope,
    GameUIMessageType,
    SetSpeedButtonLevelMessage,
    StopAutoplayClickedMessage,
    ViewportType,
    BetSliderScrollStartedMessage,
    BetSliderScrollFinishedMessage,
    BetSliderSetScrollingIndexMessage,
    BuyBonusRejectedMessage,
} from 'game-ui-types'
import { collectUltraPlayWin } from './ultraPlayCollect'
import {
    deferUltraPlayBetChange,
    executeUltraPlayPause,
    flushPendingUltraPlayPause,
    isUltraPlayPauseBlocked,
    requestUltraPlayAutoplayStop,
} from './ultraPlayPause'
import { playUltraPlaySound, ULTRA_PLAY_SOUND } from './ultraPlaySound'
import { ultraPlayAnalytics } from './ultraPlayAnalytics'
import { largestFiniteEngineIndex } from './ultraPlaySpinRecorder'

// ── Helpers ───────────────────────────────────────────────────────────────────

const getBetAmount = (betOptions: number[] | BetOptionItem[], index: number): number => {
    if (!betOptions.length) return 0

    const clamped = Math.max(0, Math.min(index, betOptions.length - 1))
    const option = betOptions[clamped]

    return typeof option === 'number' ? option : option.betAmount
}

const findClosestEngineIndex = (engineOptions: number[], targetCount: number): number => {
    if (!engineOptions.length) return 0

    // Infinite: drive the engine with its largest finite batch instead of the endless option, which
    // emits no per-spin count (so the Ultra Play table would never record). The session re-triggers.
    if (targetCount === -1) {
        return largestFiniteEngineIndex(engineOptions)
    }

    return engineOptions.reduce((best, count, i) => {
        if (count === -1) return best

        const bestCount = engineOptions[best] === -1 ? Infinity : engineOptions[best]

        return Math.abs(count - targetCount) < Math.abs(bestCount - targetCount) ? i : best
    }, 0)
}

// All Ultra Play controls logic: store wiring, derived view state and the play/pause/bet/spins/buy-
// bonus handlers. Extracted from UltraPlayControls so that component is purely presentational.
export const useUltraPlayControls = () => {
    const status = useUltraPlayStore((s) => s.status)
    const spins = useUltraPlayStore((s) => s.spins)
    const totalBet = useUltraPlayStore((s) => s.totalBet)
    const totalWin = useUltraPlayStore((s) => s.totalWin)
    const remainingSpins = useUltraPlayStore((s) => s.remainingSpins)
    const currentSpinNumber = useUltraPlayStore((s) => s.currentSpinNumber)
    const selectedBetIndex = useUltraPlayStore((s) => s.selectedBetIndex)
    const setSelectedBetIndex = useUltraPlayStore((s) => s.setSelectedBetIndex)
    const selectedSpinsIndex = useUltraPlayStore((s) => s.selectedSpinsIndex)
    const setSelectedSpinsIndex = useUltraPlayStore((s) => s.setSelectedSpinsIndex)
    const stopOnFeature = useUltraPlayStore((s) => s.stopOnFeature)
    const setStopOnFeatureState = useUltraPlayStore((s) => s.setStopOnFeature)
    const isNoConnection = useUltraPlayStore((s) => s.isNoConnection)
    const isInFeature = useUltraPlayStore((s) => s.isInFeature)
    const showFeatureChoiceDialog = useUltraPlayStore((s) => s.showFeatureChoiceDialog)
    const isBuyBonusRoundInFlight = useUltraPlayStore((s) => s.isBuyBonusRoundInFlight)
    const isAwaitingAutoplayStop = useUltraPlayStore((s) => s.isAwaitingAutoplayStop)
    const isBetInFlight = useUltraPlayStore((s) => s.isBetInFlight)
    const isPausePending = useUltraPlayStore((s) => s.isPausePending)
    const selectedBuyBonusFeatureName = useUltraPlayStore((s) => s.selectedBuyBonusFeatureName)
    const setSelectedBuyBonusFeatureName = useUltraPlayStore((s) => s.setSelectedBuyBonusFeatureName)
    const start = useUltraPlayStore((s) => s.start)
    const continueSession = useUltraPlayStore((s) => s.continueSession)
    const resume = useUltraPlayStore((s) => s.resume)
    const extendRemainingSpins = useUltraPlayStore((s) => s.extendRemainingSpins)

    const betOptions = useConfigurationStore((s) => s.betOptions)
    const setConfiguration = useConfigurationStore((s) => s.setConfiguration)
    const currency = useConfigurationStore((s) => s.currency)
    const hideCents = useConfigurationStore((s) => s.currencyHideCents)
    const hasBuyBonus = useConfigurationStore((s) => s.hasBuyBonus)
    const hasDoubleChance = useConfigurationStore((s) => s.hasDoubleChance)
    const doubleChanceMultiplier = useConfigurationStore((s) => s.doubleChanceMultiplier)
    const viewport = useConfigurationStore((s) => s.viewport)
    const paytableData = useConfigurationStore((s) => s.paytableData)
    const gameName = (paytableData.gameName as string) || ''

    const buyBonusFeatures = useBuyBonusStore((s) => s.buyBonusConfig.features)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((s) => s.updateCurrentFeatureMultiplier)
    const setMultiChoiceToggle = useBuyBonusStore((s) => s.setMultiChoiceToggle)
    const updateScrollToIndex = useBetSliderStore((s) => s.updateScrollToIndex)
    const playEngineSound = useSoundStore((s) => s.playEngineSound)
    // Same drag feedback as the autoplay-settings sliders: a rate-limited engine "tick" sound, distinct
    // from the steppers' own click sounds.
    const triggerSliderDragSound = useThrottledCallback(() => playEngineSound(SOUNDS.BET_SLIDE), 50)
    const isSetup = status === 'READY' && spins.length === 0
    const isPlaying = status === 'PLAYING'
    const isPaused = status === 'PAUSED'
    const isActive = isPlaying || isPaused
    const isHistory = status === 'READY' && spins.length > 0
    const isPortrait = viewport === ViewportType.MOBILE_PORTRAIT
    const isLandscape = viewport === ViewportType.MOBILE_LANDSCAPE
    const isPauseSettling = isAwaitingAutoplayStop && isPaused
    const isJackpotRoundUnfinished = useJackpotStore((s) => s.isJackpotRoundUnfinished)
    const playPauseDisabled =
        isNoConnection || isPauseSettling || showFeatureChoiceDialog || (!isActive && isJackpotRoundUnfinished)
    const controlsDisabled = playPauseDisabled || ((isInFeature || isBuyBonusRoundInFlight) && isPlaying)

    // Portrait only: the gear in the footer reveals the feature settings (Stop on Feature + Buy Bonus)
    // above the play button. Stays open while the footer is shown (playing/paused and the finished
    // history view); only resets when we return to the setup screen.
    const [settingsOpen, setSettingsOpen] = React.useState(false)

    React.useEffect(() => {
        if (isSetup) setSettingsOpen(false)
    }, [isSetup])

    // In the finished (history) view the spins display stays at the completed set (e.g. 10/10) until
    // the player picks a new count — then it previews the next total (the value a fresh start shows).
    const [hasPickedNewSpins, setHasPickedNewSpins] = React.useState(false)

    React.useEffect(() => {
        if (!isHistory) setHasPickedNewSpins(false)
    }, [isHistory])

    const currentBetAmount = getBetAmount(betOptions, selectedBetIndex)
    const currentSpinsCount = ULTRA_PLAY_SPIN_OPTIONS[selectedSpinsIndex] ?? 20
    const totalSpins = currentSpinNumber + remainingSpins

    const spinsValue = isActive
        ? `${currentSpinNumber}/${currentSpinsCount === -1 ? '∞' : totalSpins}`
        : isHistory
          ? hasPickedNewSpins
              ? `${currentSpinNumber}/${currentSpinsCount === -1 ? '∞' : currentSpinsCount}`
              : `${currentSpinNumber}/${currentSpinNumber}`
          : String(currentSpinsCount === -1 ? '∞' : currentSpinsCount)

    // Spins step only through the predefined options. While playing you can't lower the total below
    // the spins already played — the floor is the smallest option still above the current spin count.
    const canIncrementSpins = selectedSpinsIndex < ULTRA_PLAY_SPIN_OPTIONS.length - 1
    const canDecrementSpins =
        selectedSpinsIndex > 0 && (!isActive || ULTRA_PLAY_SPIN_OPTIONS[selectedSpinsIndex - 1] > currentSpinNumber)

    // ── Buy Bonus bet options ─────────────────────────────────────────────────
    // Save original betOptions before any buy bonus selection so we can restore on "OFF"

    const originalBetOptionsRef = React.useRef<typeof betOptions | null>(null)

    // On unmount (modal closed) restore the game's base betOptions. Ultra Play rewrites betOptions to
    // reflect a feature's multiplied bet, but the game shows the multiplied bet via currentFeature
    // multiplier instead — so the array itself must go back to base. The multiplier / selection state
    // is intentionally preserved here: it is carried over to the game on close (see UltraPlayModal).
    React.useEffect(() => {
        return () => {
            if (originalBetOptionsRef.current) {
                useConfigurationStore.getState().setConfiguration({ betOptions: originalBetOptionsRef.current })
            }
        }
    }, [])

    // Rewrite betOptions to base × multiplier so the Ultra Play bet display / recorder reflect the
    // feature cost. The base array is captured once so repeated selections always multiply from base.
    const applyMultiplierToBetOptions = (multiplier: number) => {
        if (originalBetOptionsRef.current === null) {
            originalBetOptionsRef.current = betOptions
        }

        const originals = originalBetOptionsRef.current
        const multiplied = originals.map((opt) =>
            typeof opt === 'number'
                ? opt * multiplier
                : { ...opt, betAmount: (opt as { betAmount: number }).betAmount * multiplier }
        ) as typeof betOptions

        setConfiguration({ betOptions: multiplied })
    }

    const collectWin = collectUltraPlayWin

    // Reconcile the engine's Double Chance toggle to a desired on/off state. The engine flips its
    // internal state on every DOUBLE_CHANCE_BUTTON_CLICKED, so only send when it actually differs
    // (isDoubleChanceActivated mirrors the engine state) — otherwise the toggle would invert.
    const setEngineDoubleChance = (desired: boolean) => {
        const { isDoubleChanceActivated, setIsDoubleChanceActivated } = useDoubleChanceStore.getState()

        if (isDoubleChanceActivated === desired) return

        setIsDoubleChanceActivated(desired)
        window.postMessage(
            {
                type: GameUIMessageType.DOUBLE_CHANCE_BUTTON_CLICKED,
                scope: GameUIMessageScope.DOUBLE_CHANCE,
            } as DoubleChanceButtonClickedMessage,
            '*'
        )
        collectWin()
    }

    const handleBuyBonusChange = (featureName: string) => {
        if (isActive && isUltraPlayPauseBlocked()) return

        const previousFeatureName = useUltraPlayStore.getState().selectedBuyBonusFeatureName

        playUltraPlaySound(ULTRA_PLAY_SOUND.INFO_2)

        if (featureName === 'OFF') {
            if (originalBetOptionsRef.current) {
                setConfiguration({ betOptions: originalBetOptionsRef.current })
            }

            updateCurrentFeatureMultiplier(1)
            setMultiChoiceToggle(false)

            const buyBonusRejectedMessage: BuyBonusRejectedMessage = {
                type: GameUIMessageType.BUY_BONUS_REJECTED,
            }

            window.postMessage(buyBonusRejectedMessage)
        } else if (featureName === ULTRA_PLAY_DOUBLE_CHANCE) {
            // Double Chance: multiplies the bet like a buy bonus, but is a bet-mode toggle (not a
            // multi-choice buy bonus) — the engine toggle is reconciled on play / resume / close.
            applyMultiplierToBetOptions(doubleChanceMultiplier)
            updateCurrentFeatureMultiplier(doubleChanceMultiplier)
            setMultiChoiceToggle(false)
        } else {
            const feature = buyBonusFeatures.find((f) => f.name === featureName)

            if (feature) {
                applyMultiplierToBetOptions(feature.multiplier)
                updateCurrentFeatureMultiplier(feature.multiplier)
                setMultiChoiceToggle(true)
            }
        }

        setSelectedBuyBonusFeatureName(featureName)
        ultraPlayAnalytics.featureSelected(featureName, previousFeatureName)

        // During an active session a buy bonus change pauses the game (like a bet change); the new
        // selection is sent to the engine on resume. Stop on Feature changes never pause. Mirrors
        // sendBetChange: lock the controls until the engine confirms the stop (else Resume could race a
        // new autoplay batch against it), and collect first so the in-flight spin's pending win is
        // credited — the recorder finalizes the paused bet on its real result, not a premature 0.
        if (isActive) {
            executeUltraPlayPause()
        }
    }

    // On open, reflect the game's active feature (Double Chance or a selected buy bonus) into the
    // dropdown + bet, so the two stay in sync. Runs once when the controls mount (i.e. modal opened);
    // the engine is already in this state, so this only mirrors the display — no engine messages.
    React.useEffect(() => {
        const { isDoubleChanceActivated } = useDoubleChanceStore.getState()
        const { isMultiChoiceToggled, selectedFeature } = useBuyBonusStore.getState()

        if (hasDoubleChance && isDoubleChanceActivated) {
            handleBuyBonusChange(ULTRA_PLAY_DOUBLE_CHANCE)
        } else if (isMultiChoiceToggled && selectedFeature) {
            handleBuyBonusChange(selectedFeature.name)
        }
    }, [])

    const sendBuyBonusToEngine = (featureName: string, betIdx: number) => {
        const features = useConfigurationStore.getState().buyBonusConfig.features
        const featureIndex = features.findIndex((f) => f.name === featureName)

        window.postMessage(
            {
                type: GameUIMessageType.BUY_BONUS_BUTTON_CLICKED,
                scope: GameUIMessageScope.BUY_BONUS,
            } as BuyBonusButtonClickedMessage,
            '*'
        )
        window.postMessage(
            {
                type: GameUIMessageType.BUY_BONUS_OPTION_BUTTON_CLICKED,
                featureId: featureName,
            } as BuyBonusOptionButtonClickedMessage,
            '*'
        )
        window.postMessage(
            {
                type: GameUIMessageType.BET_SLIDER_SET_SCROLLING_INDEX,
                currentBetIndex: betIdx,
                shouldPositionList: true,
            } as EngineBetSliderSetScrollingIndexMessage,
            '*'
        )
        updateScrollToIndex(betIdx)
        window.postMessage(
            {
                type: GameUIMessageType.BUY_BONUS_CONFIRM_BUTTON_CLICKED,
                selectedFeatureIndex: featureIndex,
                betIndex: betIdx,
                selectedFeatureName: featureName,
                scope: GameUIMessageScope.BUY_BONUS,
            } as BuyBonusConfirmButtonClickedMessage,
            '*'
        )
    }

    // Reconcile the engine to the selected feature before autoplay starts: enable/disable Double
    // Chance, or send the buy bonus purchase. Called from play and resume.
    const syncFeatureToEngine = (featureName: string, betIdx: number) => {
        setEngineDoubleChance(featureName === ULTRA_PLAY_DOUBLE_CHANCE)

        if (featureName !== 'OFF' && featureName !== ULTRA_PLAY_DOUBLE_CHANCE) {
            sendBuyBonusToEngine(featureName, betIdx)
        }
    }

    // ── Handlers ─────────────────────────────────────────────────────────────
    const sendBetChange = (newIndex: number) => {
        window.postMessage(
            {
                scope: GameUIMessageScope.BET_SLIDER,
                type: GameUIMessageType.BET_SLIDER_SELECTED_INDEX_CHANGED,
                index: newIndex,
                isScrolling: false,
            } as BetSliderSelectedIndexChangedMessage,
            '*'
        )
    }

    const handleBetChangedWhileActive = (newIndex: number) => {
        executeUltraPlayPause()
        sendBetChange(newIndex)
    }

    // Apply a bet index from any control (slider or +/-): update it and, during an active session,
    // pause and sync to the engine — so sliding pauses Ultra Play just like the steppers do.
    // Keep the game's own bet control (which stays mounted, just hidden, behind Ultra Play) in sync
    // live, including its scroll position — otherwise closing Ultra Play (or pressing Play) can leave
    // it highlighting the right bet but scrolled to a different one, or showing the pre-Ultra-Play bet
    // entirely. Go through BET_SLIDER_SET_SCROLLING_INDEX — the same message the engine sends when it
    // moves the bet — rather than writing betSliderStore directly: BetStepper only repositions inside
    // its own slideToIndex(), which it skips when activeIndex and scrollToIndex are already equal, so
    // setting both leaves its value strip on the old bet. This channel also maps the index into the
    // active bet group, which a raw store write does not.
    const applyBetIndexChange = (newIndex: number) => {
        setSelectedBetIndex(newIndex)

        const setScrollingIndexMessage: BetSliderSetScrollingIndexMessage = {
            scope: GameUIMessageScope.MAIN_BUTTONS,
            type: GameUIMessageType.BET_SLIDER_SET_SCROLLING_INDEX,
            currentBetIndex: newIndex,
            shouldPositionList: false,
            recovery: true,
        }

        window.postMessage(setScrollingIndexMessage)

        if (!isPlaying) {
            sendBetChange(newIndex)

            return
        }

        if (isUltraPlayPauseBlocked()) {
            deferUltraPlayBetChange(sendBetChange)
            requestUltraPlayAutoplayStop()

            return
        }

        handleBetChangedWhileActive(newIndex)
    }

    // Slider drag only (the stepper buttons call applyBetIndexChange directly, with their own sound).
    // The no-op guard keeps the slider's continuous onChange from re-pausing on every tick.
    const handleBetIndexChange = (newIndex: number) => {
        if (newIndex === useUltraPlayStore.getState().selectedBetIndex) return

        triggerSliderDragSound()
        applyBetIndexChange(newIndex)
    }

    const handleBetDecrement = () => {
        if (selectedBetIndex <= 0) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.DECREASE_BET)
        applyBetIndexChange(selectedBetIndex - 1)
    }

    const handleBetIncrement = () => {
        if (selectedBetIndex >= betOptions.length - 1) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.INCREASE_BET)
        applyBetIndexChange(selectedBetIndex + 1)

        const scrollStartedMessage: BetSliderScrollStartedMessage = {
            scope: GameUIMessageScope.BET_SLIDER,
            type: GameUIMessageType.BET_SLIDER_SCROLL_STARTED,
        }

        window.postMessage(scrollStartedMessage)

        const scrollFinishedMessage: BetSliderScrollFinishedMessage = {
            scope: GameUIMessageScope.BET_SLIDER,
            type: GameUIMessageType.BET_SLIDER_SCROLL_FINISHED,
            activeIndex: selectedBetIndex + 1,
        }

        window.postMessage(scrollFinishedMessage)
    }

    // During an active session, grow/shrink the live remaining count so the x/total display reacts
    // immediately. No engine call here — that is done via restartActiveAutoplay on commit/click.
    const extendActiveSpins = (index: number) => {
        const newCount = ULTRA_PLAY_SPIN_OPTIONS[index]

        if (newCount === -1) return

        const { currentSpinNumber: num, remainingSpins: rem } = useUltraPlayStore.getState()
        const newRemaining = Math.max(1, newCount - num)

        extendRemainingSpins(newRemaining - rem)
    }

    // Re-issue autoplay so the engine picks up the new spins count.
    const restartActiveAutoplay = () => {
        if (isUltraPlayPauseBlocked()) return

        useUltraPlayStore.getState().setPendingAutoplayRestart(true)
        window.postMessage({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage, '*')
    }

    // Spins slider drag: update the count live (display + remaining). The engine re-sync is deferred
    // to commit (slider release) so dragging doesn't restart autoplay on every tick.
    const handleSpinsIndexChange = (newIndex: number) => {
        if (newIndex === selectedSpinsIndex) return

        triggerSliderDragSound()
        setSelectedSpinsIndex(newIndex)

        if (isHistory) setHasPickedNewSpins(true)

        if (isActive) extendActiveSpins(newIndex)
    }

    // Spins slider release: now re-sync the engine to the chosen count.
    const handleSpinsCommit = () => {
        if (isPlaying && ULTRA_PLAY_SPIN_OPTIONS[useUltraPlayStore.getState().selectedSpinsIndex] !== -1) {
            restartActiveAutoplay()
        }
    }

    const handleSpinsDecrement = () => {
        if (selectedSpinsIndex <= 0) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.NUMBER_SPINS)

        const newIndex = selectedSpinsIndex - 1

        setSelectedSpinsIndex(newIndex)

        if (isHistory) setHasPickedNewSpins(true)

        if (isActive) {
            extendActiveSpins(newIndex)

            if (isPlaying && ULTRA_PLAY_SPIN_OPTIONS[newIndex] !== -1) restartActiveAutoplay()
        }
    }

    const handleSpinsIncrement = () => {
        if (selectedSpinsIndex >= ULTRA_PLAY_SPIN_OPTIONS.length - 1) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.NUMBER_SPINS)

        const newIndex = selectedSpinsIndex + 1

        setSelectedSpinsIndex(newIndex)

        if (isHistory) setHasPickedNewSpins(true)

        if (isActive) {
            extendActiveSpins(newIndex)

            if (isPlaying && ULTRA_PLAY_SPIN_OPTIONS[newIndex] !== -1) restartActiveAutoplay()
        }
    }

    const handlePlay = () => {
        // No connection — play is disabled in the UI, but guard here too so nothing slips through.
        if (useUltraPlayStore.getState().isNoConnection) return

        if (useJackpotStore.getState().isJackpotRoundUnfinished) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.PLAY)

        // Not enough funds for the selected bet — show the modal instead of starting a session.
        if (currentBetAmount > 0 && usePlayerInfoStore.getState().balance < currentBetAmount) {
            useUltraPlayStore.getState().setShowInsufficientBalance(true)
            ultraPlayAnalytics.insufficientBalance('play')

            return
        }

        syncFeatureToEngine(useUltraPlayStore.getState().selectedBuyBonusFeatureName, selectedBetIndex)

        const engineOptions = useConfigurationStore.getState().autoplayOptions
        const engineIndex = findClosestEngineIndex(engineOptions, currentSpinsCount)

        window.postMessage(
            { type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED, payload: engineIndex } as AutoplayButtonClickedMessage,
            '*'
        )

        const sbStore = useSpinButtonSectionStore.getState()

        sbStore.updateAutoplayMode(true)
        sbStore.updateAutoplayCount(currentSpinsCount !== -1 ? currentSpinsCount - 1 : currentSpinsCount)
        sbStore.updateSelectedAutoplayIndex(engineIndex)

        const currentSpeed = useSpinButtonSectionStore.getState().speedButtonLevel

        useUltraPlayStore.getState().setPreviousSpeedLevel(currentSpeed)

        // Jump straight to max speed for Ultra Play (no need to simulate Speed button clicks).
        const maxSpeedLevel = useConfigurationStore.getState().shouldEnableUltraSpeed ? 3 : 2

        window.postMessage(
            { type: GameUIMessageType.SET_SPEED_BUTTON_LEVEL, speedLevel: maxSpeedLevel } as SetSpeedButtonLevelMessage,
            '*'
        )

        // From a finished session (history) → continue: keep the table, totals and spin counter and
        // just add the newly selected spins on top (10/10 + 10 → 11/20; + 50 → 11/60). Fresh otherwise.
        if (isHistory) {
            continueSession(currentSpinsCount)
        } else {
            start(usePlayerInfoStore.getState().balance, currentSpinsCount)
        }

        ultraPlayAnalytics.sessionStarted(isHistory)
    }

    React.useEffect(() => {
        flushPendingUltraPlayPause()
    }, [isPausePending, isBetInFlight, isInFeature, isBuyBonusRoundInFlight, status, isAwaitingAutoplayStop])

    const handlePlayPause = () => {
        const up = useUltraPlayStore.getState()

        if (up.isNoConnection) return

        // A pause is still settling with the engine — ignore extra clicks (the button is also disabled
        // via controlsDisabled, this is a defensive second guard) so Resume can't race the in-flight stop.
        if (up.isAwaitingAutoplayStop && isPaused) return

        if (isPlaying) {
            // Prevent pausing at the penultimate spin because the last one may already be in-flight.
            // Avoids pausing at 10 out of 10 or similar. Instead of PAUSED the desired state is READY.
            if (up.remainingSpins !== -1 && up.remainingSpins - (up.hasUnrecordedBet ? 1 : 0) <= 0) return

            playUltraPlaySound(ULTRA_PLAY_SOUND.INFO_1)

            // A stop is already on its way to the engine (an earlier blocked click posted it). Keep the
            // pause pending and let the confirmation flush it — pausing locally now, even with nothing
            // blocking, is the race waiting on the confirmation exists to prevent: the engine may not
            // have processed the stop yet and can still charge another autoplay bet.
            if (up.isAwaitingAutoplayStop) {
                up.setPausePending(true)

                return
            }

            if (isUltraPlayPauseBlocked()) {
                requestUltraPlayAutoplayStop()

                return
            }

            executeUltraPlayPause()
        } else if (isPaused) {
            if (useJackpotStore.getState().isJackpotRoundUnfinished) return

            // The balance may have dropped (e.g. a server sync) while paused — don't resume into an
            // unaffordable bet (which crashes the engine); re-show the insufficient-balance modal.
            if (currentBetAmount > 0 && usePlayerInfoStore.getState().balance < currentBetAmount) {
                useUltraPlayStore.getState().setShowInsufficientBalance(true)
                ultraPlayAnalytics.insufficientBalance('resume')

                return
            }

            const { remainingSpins: pausedRemaining, hasUnrecordedBet } = useUltraPlayStore.getState()
            const effectiveRemaining = pausedRemaining === -1 ? -1 : pausedRemaining - (hasUnrecordedBet ? 1 : 0)

            if (effectiveRemaining !== -1 && effectiveRemaining <= 0) {
                ultraPlayAnalytics.sessionEnded('completed')
                useUltraPlayStore.getState().stop()

                return
            }

            playUltraPlaySound(ULTRA_PLAY_SOUND.PLAY)
            ultraPlayAnalytics.sessionResumed()
            resume()

            const { selectedBuyBonusFeatureName: resumeBuyBonus } = useUltraPlayStore.getState()
            const engineOptions = useConfigurationStore.getState().autoplayOptions
            const resumeEngineIndex = findClosestEngineIndex(engineOptions, effectiveRemaining)

            syncFeatureToEngine(resumeBuyBonus, selectedBetIndex)

            const maxSpeedLevel = useConfigurationStore.getState().shouldEnableUltraSpeed ? 3 : 2
            const currentSpeed = useSpinButtonSectionStore.getState().speedButtonLevel

            if (currentSpeed < maxSpeedLevel) {
                window.postMessage(
                    {
                        type: GameUIMessageType.SET_SPEED_BUTTON_LEVEL,
                        speedLevel: maxSpeedLevel,
                    } as SetSpeedButtonLevelMessage,
                    '*'
                )
            }

            window.postMessage(
                {
                    type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED,
                    payload: resumeEngineIndex,
                } as AutoplayButtonClickedMessage,
                '*'
            )

            const sbStore = useSpinButtonSectionStore.getState()

            sbStore.updateAutoplayMode(true)
            sbStore.updateAutoplayCount(effectiveRemaining === -1 ? -1 : effectiveRemaining - 1)
            sbStore.updateSelectedAutoplayIndex(resumeEngineIndex)
        } else {
            handlePlay()
        }
    }

    // Stop on Feature drives the Extra Win splash: with it OFF, Ultra Play plays through features fast
    // so Extra Win stays disabled; with it ON, a feature closes Ultra Play back to the game, so Extra
    // Win is re-enabled for normal in-game play.
    const setStopOnFeature = (value: boolean) => {
        setStopOnFeatureState(value)
        ultraPlayAnalytics.stopOnFeatureToggled(value)
        window.postMessage(
            {
                type: ExtraWinMessages.EXTRA_WIN_TOGGLED,
                payload: { status: value ? 'enabled' : 'disabled' },
            } as ExtraWinToggledMessage,
            '*'
        )
    }

    return {
        // view state
        status,
        totalBet,
        totalWin,
        currency,
        hideCents,
        gameName,
        viewport,
        isSetup,
        isPlaying,
        isLandscape,
        isPortrait,
        currentBetAmount,
        spinsValue,
        selectedBetIndex,
        setSelectedBetIndex,
        selectedSpinsIndex,
        setSelectedSpinsIndex,
        betOptions,
        stopOnFeature,
        setStopOnFeature,
        isNoConnection,
        controlsDisabled,
        playPauseDisabled,
        isPausePending,
        selectedBuyBonusFeatureName,
        buyBonusFeatures,
        hasBuyBonus,
        hasDoubleChance,
        canDecrementSpins,
        canIncrementSpins,
        settingsOpen,
        setSettingsOpen,
        // handlers
        handleBetIndexChange,
        handleBetDecrement,
        handleBetIncrement,
        handleSpinsIndexChange,
        handleSpinsCommit,
        handleSpinsDecrement,
        handleSpinsIncrement,
        handleBuyBonusChange,
        handlePlayPause,
    }
}

export type UltraPlayControlsApi = ReturnType<typeof useUltraPlayControls>
